import svgQuotePurple from '@img/icons/background/quote-purple.svg?url';
import svgQuoteWhite from '@img/icons/background/quote-white.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import { fluid } from './fluid';
import fonts from './fonts';
export const headingStyles = css `
  ${fonts.graphikCompact.semibold};

  color: currentColor;
  margin: 0 0 ${fluid(18, 26)};
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluid(32, 50)};
  line-height: ${fluid(40, 70)};
`;
export const h2 = css `
  ${headingStyles};

  font-size: ${fluid(28, 36)};
  line-height: ${fluid(36, 50)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(24, 32)};
  line-height: ${fluid(32, 44)};
`;
export const h4 = css `
  ${headingStyles};
  ${fonts.graphikCompact.medium};

  font-size: ${fluid(22, 24)};
  line-height: ${fluid(30, 34)};
`;
const paragraphStyles = css `
  ${fonts.graphikCompact.regular};
  color: currentColor;
  margin: 0 0 ${fluid(16, 24)};
`;
export const quotes = css `
  ${paragraphStyles};
  ${fonts.graphikCompact.medium};

  font-size: ${fluid(22, 24)};
  line-height: ${fluid(30, 34)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};
  ${fonts.graphikCompact.medium};

  font-size: ${fluid(20, 22)};
  line-height: ${fluid(30, 32)};
`;
export const paragraphRegular = css `
  ${paragraphStyles};

  font-size: ${fluid(18, 20)};
  line-height: ${fluid(26, 28)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(14, 16)};
  line-height: ${fluid(20, 22)};
`;
export const caption = css `
  ${paragraphSmall};
  ${fonts.graphikCompact.medium};
  text-transform: uppercase;
`;
export const label = css `
  ${paragraphStyles};
  ${fonts.graphikCompact.regular};

  font-size: ${fluid(14, 16)};
  line-height: ${fluid(18, 20)};
  text-transform: uppercase;
`;
export const link = css `
  ${transition('text-decoration-color')};

  color: currentColor;
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    text-decoration-color: transparent;
  }
`;
export const blockquote = css `
  ${paragraphStyles};
  ${fonts.graphikCompact.regular};
  color: currentColor;
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(28, 28)};
  padding-left: ${fluid(44, 88)};
  text-align: left;
  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: ${fluid(32, 64)};
    height: 100%;
    background-size: ${fluid(32, 64)};
    background-repeat: no-repeat;
    background-position: 0 5px;

    [data-variant='dark'] & {
      background-image: url(${svgQuoteWhite});
    }

    [data-variant='light'] & {
      background-image: url(${svgQuotePurple});
    }
  }
`;
export const blockquoteName = css `
  ${paragraphStyles};
  ${fonts.graphikCompact.medium};
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(28, 28)};
  padding-left: 28px;
  text-align: left;

  > strong {
    display: block;
  }
`;
export const table = css `
  border-collapse: collapse;
  margin-bottom: ${fluid(16, 24)};
  width: 100%;

  thead {
    tr {
      border-bottom: 2px solid var(--fgColourTint);

      td,
      th {
        ${fonts.graphikCompact.medium};

        font-size: ${fluid(18, 24)};
        line-height: ${fluid(28, 28)};
        padding: 6px 16px 12px;
        text-align: left;

        &:not(:last-child) {
          border-right: 1px solid var(--fgColourTint);
        }
      }
    }
  }

  tbody {
    tr {
      th {
        ${fonts.graphikCompact.medium};
        font-size: ${fluid(18, 24)};
        line-height: ${fluid(28, 28)};
        padding: 12px 16px;
        text-align: left;
      }

      td {
        ${fonts.graphikCompact.medium};
        font-size: ${fluid(18, 22)};
        line-height: ${fluid(28, 28)};
        padding: 12px 16px;
        text-align: left;

        &:not(:last-child) {
          border-right: 1px solid var(--fgColourTint);
        }
      }
    }

    tr {
      &:nth-child(even) {
        td {
          background-color: var(--bgColour);
        }
      }

      &:nth-child(odd) {
        td {
          background-color: var(--bgColourTint);
        }
      }
    }
  }
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  p {
    ${paragraphRegular}

    a {
      ${link};
    }
  }

  .large {
    ${paragraphLarge}
  }

  .small {
    ${paragraphSmall}
  }

  a {
    ${link};

    color: var(--fgColour);
  }

  ol,
  ul {
    padding-left: 1.5em;
    margin: 0 0 ${fluid(24, 32)};

    li {
      ${paragraphStyles};
      ${paragraphRegular};

      &:last-child {
        margin: 0;
      }

      margin: 0 0 ${fluid(12, 24)};

      p {
        &:last-child {
          margin-bottom: 0;
        }
      }

      ol,
      ul {
        margin-top: ${fluid(24, 32)};
      }
    }
  }

  ol {
    list-style: decimal;
  }

  ul:not([style*='list-style-type']) {
    list-style: none !important;
    padding-left: 1.5em;

    li {
      position: relative;

      &:before {
        content: '';
        position: absolute;
        left: -1.2em;
        top: 0.7em;
        height: 2px;
        width: 10px;
        background-color: var(--fgColour);
      }
    }
  }

  blockquote {
    ${blockquote};
  }

  .quote-name {
    ${blockquoteName}
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 2px;
    margin: ${fluid(32, 48)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
